<template>
  <validated-form
    name="address"
    :disabled="isSavingAddress || isProcessing"
    @valid-submit="submitAddress"
  >
    <div class="cf-flex cf-flex--column cf-padding-y-16">
      <div
        v-if="isLoadingSavedAddresses"
        class="cf-section-loader-container"
      >
        <div class="cf-loader" />
      </div>

      <div
        v-else
        class="cf-flex cf-flex--column cf-flex--gap-16"
      >
        <div class="cf-flex cf-flex--column cf-flex--gap-12">
          <div class="cf-flex cf-flex--row cf-flex--align-center cf-flex--justify-space-between">
            <h3 class="cf-title cf-title--3">
              {{ title }}
            </h3>

            <button
              v-if="currentUser.accountId && !showAddressForm"
              class="cf-button cf-button--secondary cf-button--fixed-32"
              type="button"
              title="Add Address"
              @click="startAddingNewAddress"
            >
              <span class="cf-icon cf-icon--brand-primary far fa-plus" />
            </button>
          </div>

          <div
            v-if="savedAddresses.length > 0"
            class="cf-card-list"
          >
            <label
              v-for="savedAddress in savedAddresses"
              :key="savedAddress.id"
              class="cf-card-list__item"
              :for="`selectedAddress${ savedAddress.id}`"
            >
              <div class="cf-flex cf-flex--row cf-flex--align-center">
                <div class="cf-checkbox-input cf-checkbox-input--radio cf-checkbox-input--default cf-checkbox-input--no-label-text cf-clear-margin">
                  <div class="cf-checkbox-input__option">
                    <input
                      :id="`selectedAddress${ savedAddress.id}`"
                      class="cf-checkbox-input__input"
                      type="radio"
                      name="selectedAddress"
                      :checked="selectedAddressId === savedAddress.id"
                      :value="savedAddress.id"
                      @input="selectAddress(savedAddress.id)"
                    >

                    <div class="cf-checkbox-input__label" />
                  </div>
                </div>
              </div>

              <div class="cf-flex cf-flex--column cf-text-align-left cf-max-width-300">
                <h5 class="cf-title cf-title--5">
                  {{ savedAddress.type }}
                </h5>

                <p
                  v-if="fullNameRequired && savedAddress.addressee"
                  class="cf-subtitle cf-subtitle--2"
                >
                  {{ savedAddress.addressee }}
                </p>

                <p class="cf-subtitle cf-subtitle--2 cf-no-wrap-ellipsis">
                  {{ savedAddress.line1 }},

                  <template v-if="savedAddress.line2">
                    {{ savedAddress.line2 }},
                  </template>

                  <span class="cf-no-wrap-ellipsis">
                    {{ savedAddress.city }},
                    {{ savedAddress.state }}
                    {{ savedAddress.postalCode }}
                  </span>
                </p>
              </div>

              <div class="cf-flex cf-flex--row cf-flex--align-center cf-flex--justify-end cf-flex--grow-1 cf-flex--gap-8">
                <button
                  class="cf-button cf-button--tertiary cf-button--xs"
                  title="Edit Saved Address"
                  type="button"
                  @click.prevent="selectAddressToEdit(savedAddress)"
                >
                  Edit
                </button>

                <button
                  class="cf-button cf-button--tertiary cf-button--fixed-24"
                  :class="{ 'cf-button--loading': deletingAddressId === savedAddress.id }"
                  title="Delete Saved Address"
                  type="button"
                  @click.prevent="deleteAddress(savedAddress.id)"
                >
                  <span class="cf-icon far fa-trash-alt" />

                  <span class="ada-text">
                    Delete Saved Address
                  </span>
                </button>
              </div>
            </label>
          </div>

        </div>

        <div
          v-if="showAddressForm"
          class="cf-flex cf-flex--column cf-flex--gap-16"
        >
          <cf-address-form
            v-model="address"
            :full-name-required="fullNameRequired"
          />

          <cf-radio-input
            v-if="currentUser.accountId && !addressBeingEdited && validSaveAddressTypes.length > 0"
            v-model="saveAddressAsType"
            class="cf-checkbox-input--inline"
            label="Save address as:"
            label-style="small"
            name="saveAddressAsType"
            :allow-uncheck="true"
            :options="validSaveAddressTypes"
          />
        </div>

        <div class="cf-flex cf-flex--column cf-flex--gap-16 cf-flex--align-center">
          <p
            v-if="!currentUser.accountId && merchant.options.signUpEnabled"
            class="cf-subtitle cf-subtitle--2"
          >
            Have saved addresses?
            <button
              class="cf-link cf-link--brand-secondary cf-link--heavy"
              type="button"
              @click="$openModal('signIn')"
            >
              Sign in to access them.
            </button>
          </p>

          <button
            class="cf-button cf-button--primary cf-button--lg cf-button--fill"
            :class="{ 'cf-button--loading': isSavingAddress || isProcessing }"
            type="submit"
          >
            Use Address
          </button>
        </div>
      </div>
    </div>
  </validated-form>
</template>


<script>
  import { mapGetters } from 'vuex';

  import http from '@/services/http';
  import errorHandlers from '@/services/error-handlers';

  import CfAddressForm from '@/view/components/shared/cf-address-form/index.vue';
  import CfRadioInput from '@/view/components/shared/cf-radio-input/index.vue';


  export default {
    name: 'CfDeliveryAddressForm',

    components: {
      CfAddressForm,
      CfRadioInput
    },

    props: {
      fullNameRequired: {
        type: Boolean,
        default: false
      },

      isProcessing: {
        type: Boolean,
        default: false
      },

      title: {
        type: String,
        default: 'Delivery Address'
      }
    },

    data() {
      return {
        address: {
          addressee: null,
          city: null,
          countryCode: 'US',
          line1: null,
          line2: null,
          postalCode: null,
          state: null
        },
        addressBeingEdited: null,
        deletingAddressId: null,
        isLoadingSavedAddresses: false,
        isSavingAddress: false,
        saveAddressAsType: null,
        savedAddresses: [],
        selectedAddressId: null,
        showAddressForm: true
      };
    },

    computed: {
      ...mapGetters('session', ['currentUser', 'orderDetails']),
      ...mapGetters('merchant', ['merchant']),

      validSaveAddressTypes() {
        const existingTypes = [];
        const validTypes = [];

        this.savedAddresses.forEach((address) => {
          existingTypes.push(address.type);
        });

        if (!existingTypes.includes('Home')) {
          validTypes.push({
            display: 'Home',
            value: 'Home'
          });
        }

        if (!existingTypes.includes('Work')) {
          validTypes.push({
            display: 'Work',
            value: 'Work'
          });
        }

        if (!existingTypes.includes('Other')) {
          validTypes.push({
            display: 'Other',
            value: 'Other'
          });
        }

        return validTypes;
      }
    },

    watch: {
      'currentUser.accountId': 'loadSavedAddresses'
    },

    created() {
      this.createdHandler();
    },

    methods: {
      createdHandler() {
        this.loadSavedAddresses();
      },

      async deleteAddress(id) {
        try {
          this.deletingAddressId = id;

          await http.delete(`/cardfree-api/v1/account/me/address/${id}`);

          this.savedAddresses = this.savedAddresses.filter(address => address.id !== id);

          if (id === this.selectedAddressId) {
            this.selectAddress(null);
          }
        }

        catch (error) {
          errorHandlers.generic(error);
        }

        finally {
          this.deletingAddressId = null;
        }
      },

      async loadSavedAddresses() {
        try {
          if (!this.currentUser.accountId) {
            this.selectDefaultAddress();

            return;
          }

          this.isLoadingSavedAddresses = true;

          const response = await http.get('/cardfree-api/v1/account/me/address');

          this.savedAddresses = response.data;

          this.selectDefaultAddress();
        }

        catch (error) {
          errorHandlers.generic(error);
        }

        finally {
          this.isLoadingSavedAddresses = false;
        }
      },

      async saveAddress() {
        try {
          const body = {
            addressee: this.address.addressee,
            city: this.address.city,
            countryCode: this.address.countryCode,
            line1: this.address.line1,
            line2: this.address.line2,
            postalCode: this.address.postalCode,
            state: this.address.state,
            type: this.saveAddressAsType
          };

          if (this.saveAddressAsType) {
            const response = await http.post('/cardfree-api/v1/account/me/address', body);

            this.savedAddresses.push(response.data);

            this.selectAddress(response.data.id);
          }

          else if (this.addressBeingEdited) {
            body.type = this.addressBeingEdited.type;

            const response = await http.put(`/cardfree-api/v1/account/me/address/${this.addressBeingEdited.id}`, body);

            const udpatedAddress = response.data;

            this.savedAddresses.forEach((address) => {
              if (address.id === udpatedAddress.id) {
                Object.assign(address, udpatedAddress);
              }
            });

            this.selectAddress(udpatedAddress.id);
          }
        }

        catch (error) {
          throw error;
        }
      },

      selectAddress(id) {
        this.addressBeingEdited = null;
        this.saveAddressAsType = null;
        this.selectedAddressId = id;
        this.showAddressForm = !id;
      },

      selectAddressToEdit(address) {
        Object.assign(this.address, {
          addressee: address.addressee,
          city: address.city,
          countryCode: address.countryCode,
          line1: address.line1,
          line2: address.line2,
          postalCode: address.postalCode,
          state: address.state
        });

        this.selectAddress(address.id);

        this.addressBeingEdited = address;
        this.showAddressForm = true;
      },

      selectDefaultAddress() {
        if (this.orderDetails.deliveryAddress) {
          if (this.orderDetails.deliveryAddress.id) {
            const matchingAddress = this.savedAddresses.find(address => address.id === this.orderDetails.deliveryAddress.id);

            if (matchingAddress) {
              this.selectAddress(this.orderDetails.deliveryAddress.id);
              return;
            }
          }

          this.address.addressee = this.orderDetails.deliveryAddress.addressee;
          this.address.city = this.orderDetails.deliveryAddress.city;
          this.address.countryCode = this.orderDetails.deliveryAddress.countryCode;
          this.address.line1 = this.orderDetails.deliveryAddress.line1;
          this.address.line2 = this.orderDetails.deliveryAddress.line2;
          this.address.postalCode = this.orderDetails.deliveryAddress.postalCode;
          this.address.state = this.orderDetails.deliveryAddress.state;
        }

        else if (this.savedAddresses.length > 0) {
          this.selectAddress(this.savedAddresses[0].id);
        }
      },

      startAddingNewAddress() {
        this.selectAddress(null);
      },

      async submitAddress() {
        try {
          this.isSavingAddress = true;

          await this.saveAddress();

          this.updateSelectedValue();
        }

        catch (error) {
          errorHandlers.generic(error);
        }

        finally {
          this.isSavingAddress = false;
        }
      },

      updateSelectedValue() {
        let { address } = this;

        if (this.selectedAddressId) {
          const savedAddress = this.savedAddresses.find(addr => addr.id === this.selectedAddressId);

          if (savedAddress) {
            address = savedAddress;
          }
        }

        this.$emit('selected', address);
      }
    }
  };
</script>
